/**
 * Utility to show consistent toast messages from API responses
 * Works with both the new wrapped API format and legacy responses
 */

/**
 * Shows a toast message based on an API response
 *
 * @param {Function} toast - Chakra UI toast function from useToast()
 * @param {Object} response - The API response (can be wrapped or unwrapped)
 * @param {Object} options - Additional options
 * @param {string} options.successTitle - Title for success toast (default: "Success")
 * @param {string} options.errorTitle - Title for error toast (default: "Error")
 * @param {number} options.duration - Toast duration in ms (default: 4000)
 * @param {boolean} options.isClosable - Whether toast can be closed (default: true)
 */
export const showApiToast = (toast, response, options = {}) => {
  const {
    successTitle = "Success",
    errorTitle = "Error",
    duration = 4000,
    isClosable = true,
  } = options;

  // Handle wrapped response format
  if (response && typeof response === 'object' && 'isSuccess' in response) {
    if (response.isSuccess) {
      toast({
        title: successTitle,
        description: response.response?.message || "Operation completed successfully",
        status: "success",
        duration,
        isClosable,
      });
    } else {
      toast({
        title: errorTitle,
        description: response.error?.message || "An error occurred",
        status: "error",
        duration,
        isClosable,
      });
    }
    return;
  }

  // Handle unwrapped/legacy response - assume success if we got here
  const message = response?.message || response?.description;
  if (message) {
    toast({
      title: successTitle,
      description: message,
      status: "success",
      duration,
      isClosable,
    });
  }
};

/**
 * Shows an error toast from an API error (catch block)
 * Extracts message from various error formats
 *
 * @param {Function} toast - Chakra UI toast function from useToast()
 * @param {Error|Object} error - The error object
 * @param {Object} options - Additional options
 * @param {string} options.title - Title for the toast (default: "Error")
 * @param {string} options.fallbackMessage - Fallback message if none found
 * @param {number} options.duration - Toast duration in ms (default: 4000)
 * @param {boolean} options.isClosable - Whether toast can be closed (default: true)
 */
export const showApiErrorToast = (toast, error, options = {}) => {
  const {
    title = "Error",
    fallbackMessage = "An unexpected error occurred",
    duration = 4000,
    isClosable = true,
  } = options;

  // Extract message from various error formats
  let message = fallbackMessage;

  if (error) {
    // New API format error (from unwrapApiResponse)
    if (error.message) {
      message = error.message;
    }
    // Axios error with response
    else if (error.response?.data?.message) {
      message = error.response.data.message;
    }
    // Axios error with new format in response
    else if (error.response?.data?.error?.message) {
      message = error.response.data.error.message;
    }
    // Plain string error
    else if (typeof error === 'string') {
      message = error;
    }
  }

  toast({
    title,
    description: message,
    status: "error",
    duration,
    isClosable,
  });
};

/**
 * Shows a success toast with a simple message
 *
 * @param {Function} toast - Chakra UI toast function from useToast()
 * @param {string} message - The success message
 * @param {Object} options - Additional options
 */
export const showSuccessToast = (toast, message, options = {}) => {
  const {
    title = "Success",
    duration = 3000,
    isClosable = true,
  } = options;

  toast({
    title,
    description: message,
    status: "success",
    duration,
    isClosable,
  });
};

/**
 * Shows a success toast from an API response
 * Extracts message from the response if available (from _originalResponse or response.message)
 *
 * @param {Function} toast - Chakra UI toast function from useToast()
 * @param {Object} response - The axios response object (may have _originalResponse from interceptor)
 * @param {Object} options - Additional options
 * @param {string} options.title - Title for the toast (default: "Success")
 * @param {string} options.fallbackMessage - Fallback message if none in response
 * @param {number} options.duration - Toast duration in ms (default: 3000)
 * @param {boolean} options.isClosable - Whether toast can be closed (default: true)
 */
export const showApiSuccessToast = (toast, response, options = {}) => {
  const {
    title = "Success",
    fallbackMessage = "Operation completed successfully",
    duration = 3000,
    isClosable = true,
  } = options;

  // Extract message from various response formats
  let message = fallbackMessage;

  if (response) {
    // Check _originalResponse (preserved by axios interceptor for new format)
    if (response._originalResponse?.response?.message) {
      message = response._originalResponse.response.message;
    }
    // Check if response.data has a message (legacy format)
    else if (response.data?.message) {
      message = response.data.message;
    }
    // Check direct message property
    else if (response.message) {
      message = response.message;
    }
  }

  toast({
    title,
    description: message,
    status: "success",
    duration,
    isClosable,
  });
};

export default showApiToast;
